import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Delta, EmptyPanel, ErrorPanel, LoadingPanel } from "@/components/rank/States";
import { fetchTrending } from "@/lib/api";
import { compactCount, formatScore, type TrendingAccount } from "@/lib/types";

const TIERS = ["All", "Elite", "Strong", "Watch"] as const;
const RANGES = [
  { key: "7D", label: "7D" },
  { key: "30D", label: "30D" },
] as const;

function Row({ r, range }: { r: TrendingAccount; range: (typeof RANGES)[number]["key"] }) {
  const delta = range === "7D" ? r.weekDelta : r.monthDelta;

  return (
    <li className="border-b border-line">
      <Link
        to="/profile/$handle"
        params={{ handle: r.user.username }}
        className="group grid grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3 px-3 py-3 transition-colors hover:bg-bg md:grid-cols-[48px_minmax(0,1.6fr)_minmax(0,1fr)_84px_92px] md:gap-4 md:px-4"
      >
        <span className="num text-[12px] text-ink-muted">{String(r.rank).padStart(2, "0")}</span>

        <div className="flex min-w-0 items-center gap-3">
          <span
            aria-hidden="true"
            className="mono flex size-8 shrink-0 items-center justify-center border border-ink/25 bg-bg text-[11px] font-semibold text-ink-soft group-hover:border-ink"
          >
            {r.user.username.charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <span className="mono block truncate text-[13px] font-semibold text-ink group-hover:text-accent-deep">
              @{r.user.username}
              {r.user.verified ? " ✓" : ""}
            </span>
            <p className="truncate text-[12px] text-ink-muted">
              {r.user.display_name} · {compactCount(r.user.followers_count)} followers
            </p>
          </div>
        </div>

        <div className="hidden md:block">
          <div className="flex items-center justify-between">
            <span className="label">Signal</span>
            <span className="num text-[12px] text-ink-soft">{r.signalRatio}%</span>
          </div>
          <div className="mt-1.5 h-1.5 w-full border border-ink/15 bg-bg">
            <div className="h-full bg-accent" style={{ width: `${r.signalRatio}%` }} />
          </div>
        </div>

        <span className="num hidden text-right text-[18px] font-semibold text-ink md:block">
          {formatScore(r.score)}
        </span>

        <span className="text-right">
          <Delta value={delta} />
        </span>
      </Link>
    </li>
  );
}

/** Filters + ranked table. Shared by /trending and /leaderboard. */
export function TrendingBoard() {
  const [tier, setTier] = useState<(typeof TIERS)[number]>("All");
  const [range, setRange] = useState<(typeof RANGES)[number]["key"]>("7D");
  const [rows, setRows] = useState<TrendingAccount[] | null>(null);
  const [error, setError] = useState("");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setError("");
    fetchTrending()
      .then((data) => alive && setRows(data))
      .catch((e: unknown) =>
        alive ? setError(e instanceof Error ? e.message : "Unknown error") : null,
      );
    return () => {
      alive = false;
    };
  }, [nonce]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const byTier = tier === "All" ? [...rows] : rows.filter((r) => r.tier === tier.toLowerCase());
    // Sorsa /score-changes already returns both windows — sort client-side by the selected delta.
    byTier.sort((a, b) => {
      const da = range === "7D" ? a.weekDelta : a.monthDelta;
      const db = range === "7D" ? b.weekDelta : b.monthDelta;
      if (db !== da) return db - da;
      return b.score - a.score;
    });
    return byTier.map((r, i) => ({ ...r, rank: i + 1 }));
  }, [rows, tier, range]);

  return (
    <>
      <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 border-y border-line py-4">
        <div className="flex items-center gap-3">
          <span className="label">Tier</span>
          <div className="flex flex-wrap gap-2">
            {TIERS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTier(t)}
                aria-pressed={tier === t}
                className={`mono border px-3 py-1.5 text-[12px] tracking-[0.08em] uppercase ${
                  tier === t
                    ? "border-ink bg-ink text-bg"
                    : "border-line text-ink-soft hover:border-ink hover:text-ink"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="label">Momentum</span>
          <div className="flex gap-2">
            {RANGES.map((rg) => (
              <button
                key={rg.key}
                type="button"
                onClick={() => setRange(rg.key)}
                aria-pressed={range === rg.key}
                title={
                  rg.key === "7D"
                    ? "Sort by 7-day score change (Sorsa week_delta)"
                    : "Sort by 30-day score change (Sorsa month_delta)"
                }
                className={`mono border px-3 py-1.5 text-[12px] tracking-[0.08em] uppercase ${
                  range === rg.key
                    ? "border-ink bg-accent text-accent-ink"
                    : "border-line text-ink-soft hover:border-ink hover:text-ink"
                }`}
              >
                {rg.label}
              </button>
            ))}
          </div>
        </div>

        <span className="mono ml-auto text-[12px] text-ink-muted">
          {rows ? `${filtered.length} rows` : "loading…"}
        </span>
      </div>

      <div className="mt-8">
        {error ? (
          <ErrorPanel message={error} onRetry={() => setNonce((n) => n + 1)} />
        ) : !rows ? (
          <LoadingPanel label="Loading index" />
        ) : filtered.length === 0 ? (
          <EmptyPanel title="No accounts in this tier" hint="Try another tier filter." />
        ) : (
          <section className="border border-ink bg-bg-panel">
            <div className="hidden grid-cols-[48px_minmax(0,1.6fr)_minmax(0,1fr)_84px_92px] gap-4 border-b border-ink px-4 py-3 md:grid">
              <span className="label">#</span>
              <span className="label">Handle</span>
              <span className="label">Signal ratio</span>
              <span className="label text-right">Score</span>
              <span className="label text-right">{range}</span>
            </div>
            <ul key={`${range}-${tier}`} className="board-redraw">
              {filtered.map((r) => (
                <Row key={r.user.id} r={r} range={range} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}
